import { SelectionManager } from "@/types/SelectionManager";
import { ColorDefinition, DataVisualization } from "@/types/colors";
import { SankeyOptions } from "@/types/sankeyOptions";
import { CategoryData, Co2MaterialType, ElementData, MaterialType, ModelData, ProjectData, ScenarioData } from "@api/apiClient";
import i18n from "@i18n/i18n";
import bucketize, { Buckets } from "@utils/bucketize";
import { findMapKey } from "@utils/mapUtils";
import _ from "lodash";
import mitt from "mitt";
import "@api/apiClient-extensions";
import { to } from "@react-spring/web";

// @ts-ignore
import alasql from "alasql";

export { };
declare global {
  interface Window {
    ProjectAnalysis: ProjectAnalysis;
  }
}

export enum GroupType {
  Scenario = 'Scenario',
  Model = 'Model'
}

export enum AnalysisType {
  CadModelCategory = 'CadModelCategory',
  CadMaterial = 'CadMaterial',
  CadMaterialCategory = 'CadMaterialCategory', // WIP
  Co2MaterialType = 'Co2MaterialType',
  Co2MaterialCategory = 'Co2MaterialCategory', // WIP
  ModelGroup = 'ModelGroup', // WIP
  Co2eIntensity = 'Co2eIntensity',
  Sankey = 'Sankey',
  /**
   * For mapping viewer selections to analysis selections.
   */
  LmViewer = 'LmViewer',
  System = "System",
  SubSystem = "SubSystem",
  Attribute1 = "Attribute1",
  Attribute2 = "Attribute2",
  Attribute3 = "Attribute3",
}

export const ElementPropertyToAnalysisType = new Map<string, AnalysisType>([
  ['cadModelCategoryId', AnalysisType.CadModelCategory],
  ['cadMaterial', AnalysisType.CadMaterial],
  ['cadMaterialCategoryId', AnalysisType.CadMaterialCategory],
  ['co2MaterialTypeId', AnalysisType.Co2MaterialType],
  ['co2MaterialCategoryId', AnalysisType.Co2MaterialCategory],
  ['modelGroupId', AnalysisType.ModelGroup],
  ['co2eIntensity', AnalysisType.Co2eIntensity],
  ['system', AnalysisType.System],
  ['subSystem', AnalysisType.SubSystem],
  ['freeText1', AnalysisType.Attribute1],
  ['freeText2', AnalysisType.Attribute2],
  ['freeText3', AnalysisType.Attribute3],
]);

export enum UnitType {
  tCO2e = 'tCO2e',
  KgCO2ePerM2 = 'KgCO2e/m2',
  KgCO2ePerM2PerYear = 'KgCO2e/m2/year',  
}

export type AnalysisSelectionType = AnalysisType;

export type ScenarioId = string;
export type ModelId = string;
export type GroupKey = number | string;
export type HexColor = string;
export type CadIds = Array<string>;
export type Urn = string;

export type ModelBucketCadIds = Map<string, Map<number, Array<string>>>;

const EmptyGuid = '00000000-0000-0000-0000-000000000000';
const TOTALCO2_BUCKET_SIZE = 6;
const EstimatedRebarCadMaterial = "Estimated Rebar";
const EstimatedNonModelledCadMaterial = "Estimated Non-Modelled";
const EstimatedRebarCo2MaterialTypeId = "E1F8E4B2-6F7C-47AA-9B92-E0D5D2C6B931";
const EstimatedNonModelledCo2MaterialTypeId = "4A1B6230-5B17-4A9F-8A4D-9FC9229F558E";

export class AnalysisItem {
  /**
   * @remarks
   * oneOf: 
   *  cadModelCategoryId
   *  cadMaterial
   *  cadMaterialCategoryId
   *  co2MaterialTypeId
   *  co2MaterialCategoryId
   *  modelGroupId (aka scopeId)
   *  co2eIntensity
   */
  groupKey: GroupKey = '';
  name: string = '';
  elementCount: number = 0;
  lengthTotal: number = 0;
  areaTotal: number = 0;
  volumeTotal: number = 0;
  //massTotal: number = 0;
  //quantityTotal: number = 0;
  co2eTotal: number = 0;
  minCo2e: number = 0;
  maxCo2e: number = 0;

  constructor(groupKey: GroupKey, name: string) {
    this.groupKey = groupKey;
    this.name = name;
  }
}

export class GroupAnalysis {
  analysisType: AnalysisType = AnalysisType.CadModelCategory;
  groupType: GroupType = GroupType.Scenario;
  groupIndex: number = 0;

  constructor(id: ModelId | ScenarioId, groupIndex: number, name: string, analysisType: AnalysisType, groupType: GroupType) {
    this.id = id;
    this.groupIndex = groupIndex;
    this.name = name;
    this.analysisType = analysisType;
    this.groupType = groupType;
  }

  /**
   * @remarks
   * modelId or scenarioId
   */
  id: ModelId | ScenarioId = '';
  /**
   * @remarks
   * name of the model or scenario
   */
  name: string = '';
  items: Map<GroupKey, AnalysisItem> = new Map<GroupKey, AnalysisItem>();
  /** 
   * @remarks
   * sum of all items.co2eTotal
   */
  co2eTotal: number = 0;
}

export class ScenarioAnalysis {
  scenarioId: ScenarioId = '';
  scenarioIndex: number = 0;
  name: string = '';
  groupType: GroupType = GroupType.Scenario;
  totalFloorArea? : number;
  serviceLife? : number;

  constructor(scenarioId: ScenarioId, scenarioIndex: number, name: string, groupType: GroupType, totalFloorArea?: number, serviceLife?: number) {
    this.scenarioId = scenarioId;
    this.scenarioIndex = scenarioIndex;
    this.name = name;
    this.groupType = groupType;
    this.totalFloorArea = totalFloorArea;
    this.serviceLife = serviceLife;
    this.co2eIntensity = new GroupAnalysis(scenarioId, scenarioIndex, name, AnalysisType.Co2eIntensity, GroupType.Scenario);
  }

  /**
   * @remarks
   * Contains AnalysisType.Co2eIntensity type analysis always by *Scenario* to be used in the TotalCO2 viewer extension.
   * The GroupAnalysis.Id will be a scenarioId.
   * Will need to be updated when model selection changes for a given scenario from the results grid or when
   * switching views.
   */
  co2eIntensity: GroupAnalysis;
  /**
   * @remarks
   * Contains the GroupAnalysis results. 
   * If GroupType.Scenario, then there will be a single aggregate GroupAnalysis for all selected models in the scenario.
   * If GroupType.Model, then there will be a GroupAnalysis for each selected model in the scenario.
   */
  groupData: Array<GroupAnalysis> = [];
  /**
   * @remarks
   * Contains the color assignment for each row in the results grid represented by GroupAnalysis.items[]
   */
  groupColorMap: Map<GroupKey, ColorDefinition> = new Map<GroupKey, ColorDefinition>();
  /**
   * @remarks
   * Contains the associated cadIds for each row in the results grid represented by GroupAnalysis.items[]
   * grouped by model.
   */
  modelGroupCadIdsMap: Map<Urn, Map<GroupKey, CadIds>> = new Map<Urn, Map<GroupKey, CadIds>>();
}

export class AnalysisData {
  analysisType: AnalysisType = AnalysisType.CadModelCategory;
  groupType: GroupType = GroupType.Scenario;
  /**
   * @remarks
   * usage: scenarioAnalyses[scenarioId].co2eIntensity
   * 
   * or
   * 
   * usage: scenarioAnalyses[scenarioId].groupData
   */
  scenarioAnalyses: Map<ScenarioId, ScenarioAnalysis> = new Map<ScenarioId, ScenarioAnalysis>();

  constructor(analysisType: AnalysisType, groupType: GroupType) {
    this.analysisType = analysisType;
    this.groupType = groupType;
  }

  getGroupKeys = (): Array<GroupKey> => {
    const items = Array.from(this.scenarioAnalyses.values())[0]?.groupData[0].items;
    return Array.from(items.keys());
  };

  getItemNames = (): Array<GroupKey> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return [];
    }
    const groupDataEntry = scenarioEntry[1].groupData.entries().next().value;
    if (!groupDataEntry) {
      return [];
    }
    const items: Map<GroupKey, AnalysisItem> = groupDataEntry[1].items;
    return Array.from(items).map(([_key, value]) => value.name);
  };

  getFilteredItems = (filter: (item: AnalysisItem) => boolean): Array<AnalysisItem> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return [];
    }
    const groupDataEntry = scenarioEntry[1].groupData.entries().next().value;
    if (!groupDataEntry) {
      return [];
    }
    const items: Map<GroupKey, AnalysisItem> = groupDataEntry[1].items;
    const values = Array.from(items).map(([_key, value]) => value);
    return values.filter((item: AnalysisItem) => filter(item));
  };

  colorDefinitions = (): Array<ColorDefinition> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return [];
    }
    const groupColorMap: Map<GroupKey, ColorDefinition> = scenarioEntry[1].groupColorMap;
    return Array.from(groupColorMap.entries()).map(([_key, value]) => value);
  };

   /**
   * Returns the color map for this analysis mapped by GroupKey (categoryId, materialId or just nodeId for example)
   * This allows lookup of color by ID rather than using array index as compared to colorDefinitions()
   * @returns Map<GroupKey, ColorDefinition> or empty Map if no scenarios exist
   */
  getColorMap = (): Map<GroupKey, ColorDefinition> => {
    const scenarioEntry = this.scenarioAnalyses.entries().next().value;
    if (!scenarioEntry) {
      return new Map<GroupKey, ColorDefinition>();
    }
    return scenarioEntry[1].groupColorMap;
  };

  get groupTitle() {
    switch (this.analysisType) {
      case AnalysisType.CadModelCategory:
        return i18n.t('analysis.group-title.category');
      case AnalysisType.CadMaterial:
        return i18n.t('analysis.group-title.cad-material');
      case AnalysisType.CadMaterialCategory:
        return i18n.t('analysis.group-title.cad-material-category');
      case AnalysisType.Co2MaterialType:
        return i18n.t('analysis.group-title.co2-material-type');
      case AnalysisType.Co2MaterialCategory:
        return i18n.t('analysis.group-title.co2-material-category');
      case AnalysisType.ModelGroup:
        return i18n.t('analysis.group-title.model-group');
      case AnalysisType.Co2eIntensity:
        return i18n.t('analysis.group-title.co2-intensity');
      case AnalysisType.Sankey:
        return i18n.t('analysis.group-title.sankey');
      default:
        return i18n.t('analysis.group-title.default');
    }
  }
}

export class SankeyData {
  graph = {
    nodes: [],
    links: [],
  };

  constructor(graph: any) {
    this.graph = graph;
  }
}

export enum ProjectAnalysisEvents {
  ANALYSIS_CHANGED_EVENT = "analysisChanged",
  SANKEY_ANALYSIS_CHANGED_EVENT = "sankeyAnalysisChanged",
  SELECTION_CHANGED_EVENT = "selectionChanged",
  CONFIGURATION_CHANGED_EVENT = "configurationChanged",
  UNIT_SELECTION_CHANGED_EVENT = "unitSelectionChanged"
}

/**
 * The `ProjectAnalysis` class is responsible for managing and updating analysis data for a project.
 * It handles various types of analyses, including scenario, CAD model category, CAD material, CO2 material type, and more.
 * The class also manages selections and emits events when analyses or selections change.
 * 
 * ProjectAnalysis UML Diagrams:
 * - https://lucid.app/lucidchart/471e987d-7685-479d-9e80-0b49b339c616/edit?viewport_loc=-7180%2C-3003%2C22560%2C11339%2CHWEp-vi-RSFO&invitationId=inv_0a778aba-3970-4926-994f-3f02e8e27603
 * - https://lucid.app/lucidchart/471e987d-7685-479d-9e80-0b49b339c616/view?page=HWEp-vi-RSFO#
 * 
 * @class ProjectAnalysis
 * 
 * @property {GroupType} groupType - The type of group for the analysis, defaulting to `GroupType.Scenario`.
 * @property {AnalysisType} analysisType - The type of analysis, defaulting to `AnalysisType.CadModelCategory`.
 * @property {Map<AnalysisType, AnalysisData>} analyses - A map storing analysis data for different analysis types.
 * @property {Map<AnalysisType, Set<GroupKey>>} selections - A map storing selections for different analysis types.
 * @property {Map<string, Set<string>>} modelToCadIdMap - A map storing model IDs to CAD IDs.
 * @property {any} emitter - An event emitter for emitting analysis and selection change events.
 * 
 * @constructor
 * Initializes a new instance of the `ProjectAnalysis` class and assigns it to the global `window` object.
 * 
 * @method updateAnalysis
 * Updates the analysis data based on the current selection, project data, and CO2 material types.
 * Emits an `ANALYSIS_CHANGED_EVENT` after updating the analysis.
 * 
 * @method addAnalysis
 * Adds a new analysis based on the current selection, project data, CO2 material types, and specified analysis type.
 * 
 * @method updateSankeyAnalysis
 * Updates the Sankey analysis data based on the current selection, project data, CO2 material types, and options.
 * Emits a `SANKEY_ANALYSIS_CHANGED_EVENT` after updating the analysis.
 * 
 * @method setGroupType
 * Sets the group type for the analysis and updates the analysis data if the group type has changed.
 * Emits an `ANALYSIS_CHANGED_EVENT` after updating the analysis.
 * 
 * @method setAnalysisType
 * Sets the analysis type and updates the analysis data if the analysis type has changed.
 * 
 * @method addSelection
 * Adds a selection for the specified analysis type based on the current viewer selection, project data, and model-to-CAD ID map.
 * Emits a `SELECTION_CHANGED_EVENT` if the selection has changed.
 * 
 * @method getSelections
 * Retrieves a set of unique model element values for a given property based on the current selections (modelToCadIdMap).
 * 
 * @method getSelectionsFromFilter
 * Retrieves a set of unique property values for a given property based on the current selections.
 */
export class ProjectAnalysis {
  groupType: GroupType = GroupType.Scenario;
  analysisType: AnalysisType = AnalysisType.CadModelCategory;
  analyses: Map<AnalysisType, AnalysisData> = new Map<AnalysisType, AnalysisData>();
  selectedUnitType : UnitType = UnitType.KgCO2ePerM2; //Default value
  currentSelectionType: AnalysisSelectionType | null = null;
  selections: Map<AnalysisType, Set<GroupKey>> = new Map<AnalysisType, Set<GroupKey>>();
  modelToCadIdMap: Map<string, Set<string>> = new Map<string, Set<string>>();

  totalCo2Config: string[] = ['totalCo2'];

  emitter: any = mitt();

  constructor() {
    window.ProjectAnalysis = this;
  }

  get isGroupTypeScenario() {
    return this.groupType === GroupType.Scenario;
  }

  get isGroupTypeModel() {
    return this.groupType === GroupType.Model;
  }

  /**
   * Sets the total CO2 configuration based on the specified columns.
   * @param columns The columns to include in the total CO2 configuration.
   * Valid values are: 'a1A2A3', 'a4A5', 'a', 'b1', 'b2B5', 'b', 'biogenic', 'c', 'd', 'totalCo2'
   */
  setTotalCo2Config = (columns: string[]) => {
    const totalCo2Configurator = new TotalCo2Configurator();
    const newTotalCo2Config = totalCo2Configurator.getTotalCo2Config(columns);

    if (_.isEqual(this.totalCo2Config, newTotalCo2Config) === false) {
      this.totalCo2Config = newTotalCo2Config;
      this.emitter.emit(ProjectAnalysisEvents.CONFIGURATION_CHANGED_EVENT);
    }
  };

  /**
   * Gets the analysis data for the specified analysis type.
   * 
   * @param analysisType The type of analysis to retrieve.
   * @returns The analysis data for the specified analysis type or `undefined` if no analysis data exists.
   */
  getAnalysis = (analysisType: AnalysisType): AnalysisData | undefined => {
    return this.analyses.get(analysisType);
  };

  /**
   * Gets the current selections by analysis type.
   * 
   * @param analysisType The type of analysis to retrieve the selections for.
   * @returns The current selections by analysis type or an empty set if no selections exist.
   */
  getSelections = (analysisType: AnalysisType): Set<GroupKey> => {
    return this.selections.get(analysisType) || new Set();
  };

  // TODO: if a scenario selection changes, update the scenarioAnalysis data
  // TODO: need a scenario selection change detector to compare current
  // scenario selection with the ProjectAnalysis scenario selection (use a hash?)
  // TODO: if any model data or assignment changes, update the analysis data

  updateAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    // Assumption here is that selection has changed and now all existing data is invalid.
    this.reset();

    this.updateAnalyses(selectionManager, projectData, co2MaterialTypes, this.analysisType);

    this.emitter.emit(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT);
  };

  addAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    analysisType: AnalysisType) => {

    this.updateAnalyses(selectionManager, projectData, co2MaterialTypes, analysisType);
  };

  setUnitType = (unitType: UnitType) => {
    this.selectedUnitType = unitType;
    this.emitter.emit(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, unitType);
  }

  updateSankeyAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    options: SankeyOptions) => {
    this.analyses.delete(AnalysisType.Sankey);

    this.updateSankeyAnalyses(selectionManager, projectData, co2MaterialTypes, options);

    this.emitter.emit(ProjectAnalysisEvents.SANKEY_ANALYSIS_CHANGED_EVENT);
  };

  setGroupType = (
    groupType: GroupType,
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    if (this.groupType !== groupType) {
      this.groupType = groupType;
      this.reset();

      this.updateAnalyses(selectionManager, projectData, co2MaterialTypes, this.analysisType);

      this.emitter.emit(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT);
    }
  };

  setAnalysisType = (
    analysisType: AnalysisType,
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    if (this.analysisType !== analysisType) {
      this.analysisType = analysisType;

      this.updateAnalyses(selectionManager, projectData, co2MaterialTypes, this.analysisType);
    }
  };

  setCurrentSelectionType = (analysisSelectionType: AnalysisSelectionType) => {
    this.currentSelectionType = analysisSelectionType;
  };

  addSelections = (
    analysisType: AnalysisType,
    selections: Set<GroupKey>) => {

    switch (analysisType) {
      case AnalysisType.CadModelCategory:
        this.selections.set(AnalysisType.CadModelCategory, selections);
        break;
      case AnalysisType.CadMaterial:
        this.selections.set(AnalysisType.CadMaterial, selections);
        break;
      case AnalysisType.CadMaterialCategory:
        //this.selections.set(AnalysisType.CadMaterial, selections);
        break;
      case AnalysisType.Co2MaterialType:
        this.selections.set(AnalysisType.Co2MaterialType, selections);
        break;
      case AnalysisType.Co2MaterialCategory:
        //this.selections.set(AnalysisType.Co2MaterialCategory, selections);
        break;
      case AnalysisType.Co2eIntensity:
        //this.selections.set(AnalysisType.Co2eIntensity, selections);
        break;
      case AnalysisType.System:
        this.selections.set(AnalysisType.System, selections);
        break;
      case AnalysisType.SubSystem:
        this.selections.set(AnalysisType.SubSystem, selections);
        break;
      case AnalysisType.Attribute1:
        this.selections.set(AnalysisType.Attribute1, selections);
        break;
      case AnalysisType.Attribute2:
        this.selections.set(AnalysisType.Attribute2, selections);
        break;
      case AnalysisType.Attribute3:
        this.selections.set(AnalysisType.Attribute3, selections);
        break;
      default:
        break;
    }

    this.setCurrentSelectionType(analysisType as any);

    this.emitter.emit(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT);
  };

  addSelectionsFromModelToCadIdMap = (
    analysisType: AnalysisType,
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    modelToCadIdMap: Map<string, Set<string>> | null | undefined = null) => {
    let hasSelectionChanged = false;

    // When switching GroupType, we can use the existing modelToCadIdMap
    // to generate a new selection set. When selection changes, we'll provide a new
    // modelToCadIdMap to generate a new selection set.
    if (modelToCadIdMap) {
      this.modelToCadIdMap = modelToCadIdMap;
      this.selections.clear();
      hasSelectionChanged = true;
    }

    switch (analysisType) {
      case AnalysisType.CadModelCategory:
        if (!this.hasSelection(AnalysisType.CadModelCategory)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'cadModelCategoryId');
          this.selections.set(AnalysisType.CadModelCategory, selections);
        }
        break;
      case AnalysisType.CadMaterial:
        if (!this.hasSelection(AnalysisType.CadMaterial)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'cadMaterial');
          this.selections.set(AnalysisType.CadMaterial, selections);
        }
        break;
      case AnalysisType.CadMaterialCategory:
        if (!this.hasSelection(AnalysisType.CadMaterialCategory)) {
          //const selections = this.getSelectionsFromModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'cadMaterialCategoryId');
          //this.selections.set(AnalysisType.CadMaterial, selections);
        }
        break;
      case AnalysisType.Co2MaterialType:
        if (!this.hasSelection(AnalysisType.Co2MaterialType)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'co2MaterialTypeId');
          this.selections.set(AnalysisType.Co2MaterialType, selections);
        }
        break;
      case AnalysisType.Co2MaterialCategory:
        if (!this.hasSelection(AnalysisType.Co2MaterialCategory)) {
          //const selections = this.getSelectionsFromModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'co2MaterialCategoryId');
          //this.selections.set(AnalysisType.Co2MaterialCategory, selections);
        }
        break;
      case AnalysisType.Co2eIntensity:
        //if (!this.hasSelection(AnalysisType.Co2eIntensity)) {
        //  const selections = this.getSelectionsFromModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'totalCo2'); // TODO: this will need some extra lookups to convert totalCo2 to bucketIndex?
        //  this.selections.set(AnalysisType.Co2eIntensity, selections);
        //}
        break;
      case AnalysisType.System:
        if (!this.hasSelection(AnalysisType.System)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'system');
          this.selections.set(AnalysisType.System, selections);
        }
        break;
      case AnalysisType.SubSystem:
        if (!this.hasSelection(AnalysisType.SubSystem)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'subSystem');
          this.selections.set(AnalysisType.SubSystem, selections);
        }
        break;
      case AnalysisType.Attribute1:
        if (!this.hasSelection(AnalysisType.Attribute1)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'freeText1');
          this.selections.set(AnalysisType.Attribute1, selections);
        }
        break;
      case AnalysisType.Attribute2:
        if (!this.hasSelection(AnalysisType.Attribute2)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'freeText2');
          this.selections.set(AnalysisType.Attribute2, selections);
        }
        break;
      case AnalysisType.Attribute3:
        if (!this.hasSelection(AnalysisType.Attribute3)) {
          const selections = this.getSelectedElementsByModelToCadIdMap(selectionManager, projectData, this.modelToCadIdMap, 'freeText3');
          this.selections.set(AnalysisType.Attribute3, selections);
        }
        break;
      default:
        break;
    }

    if (hasSelectionChanged) {
      this.emitter.emit(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT);
    }
  };

  /**
   * Retrieves a set of unique model element values for a given property based on the current selections (modelToCadIdMap).
   * 
   * @param selectionManager - The selection manager containing the current scenario and model selections.
   * @param projectData - The project data containing scenario and model information.
   * @param analysisType - The type of analysis to retrieve selections for (e.g., CadModelCategory, CadMaterial, Co2MaterialType).
   * @returns A set of unique model element values for a given property based on the current selections.
   */
  getSelectedElementsByAnalysisType = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    analysisType: AnalysisType): Set<GroupKey> => {
    const currentSelections = projectAnalysis.selections.get(this.currentSelectionType as any) || new Set();
    const filterKey = findMapKey(ElementPropertyToAnalysisType, (key: string) => key === this.currentSelectionType) as keyof ElementData;
    const property = findMapKey(ElementPropertyToAnalysisType, (key: string) => key === analysisType) as keyof ElementData;
    const selections = projectAnalysis.getSelectedElementsByFilter(selectionManager, projectData, property, filterKey, [...currentSelections] as any);
    return selections;
  };


  /**
     * Retrieves a Set of unique property values for a given property
     * 
     * @param projectData - The project data containing scenario and model information.
     * @param property - The property to retrieve unique values for (e.g., cadModelCategoryId, cadMaterial, co2MaterialTypeId, totalCo2).
     * @param filterKey - The property to filter the results by (e.g., cadModelCategoryId, cadMaterial, co2MaterialTypeId).
     * @param filterValues - The values to filter the results by (e.g., [cadModelCategoryId1, cadModelCategoryId2]).
     * @returns A Set of unique values for the given property based on the current selections.
     */
  getSelectedElementsByFilter = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    property: keyof ElementData,
    filterKey: keyof ElementData,
    filterValues: string[] | number[]): Set<GroupKey> => {
    const values = new Set<string | number>();

    if (!projectData) {
      return values;
    }

    this.processModelData(projectData, selectionManager, async (_scenarioData: ScenarioData, modelData: ModelData) => {
      const modelId = modelData.model?.apsViewId;
      if (modelId) {
        const sql = `SELECT DISTINCT ${property as string} FROM ? WHERE ${filterKey as string} IN @(?)`;
        const results = alasql(sql, [modelData.elementDatas, filterValues]) as unknown[];
        results.forEach((elementData: any) => elementData[property as string] && values.add(elementData[property as string]));
      }
    });

    console.log(`${property} values: `, values);
    return values;
  };

 applyUnitConversion = (value: number, scenarioAnalysis: ScenarioAnalysis): number => {
  
  const unitType = this.selectedUnitType;
  const totalFloorArea = scenarioAnalysis.totalFloorArea || 1;
  const serviceLife = scenarioAnalysis.serviceLife || 1;
  
  switch(unitType) {
    case UnitType.KgCO2ePerM2:
      return (value * 1000) / totalFloorArea;
    case UnitType.KgCO2ePerM2PerYear:
      return (value * 1000) / (totalFloorArea * serviceLife);
    case UnitType.tCO2e:
      return value; // No conversion
  }
}

  /**
   * Retrieves a Set of unique Model Element values for a given property based on the current viewer selections (modelToCadIdMap)
   * 
   * @param projectData - The project data containing scenario and model information.
   * @param modelToCadIdMap - A map of model IDs to CAD IDs retrieved from LmViewer3D.getCadIdsFromSelections.
   * @param property - The property to retrieve unique values for (e.g., cadModelCategoryId, cadMaterial, co2MaterialTypeId, totalCo2).
   * @returns A Set of unique values for the given property based on the current selections.
   */
  private getSelectedElementsByModelToCadIdMap = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    modelToCadIdMap: Map<string, Set<string>>,
    property: keyof ElementData | string): Set<string | number> => {
    const values = new Set<string | number>();

    if (!projectData) {
      return values;
    }

    this.processModelData(projectData, selectionManager, async (_scenarioData: ScenarioData, modelData: ModelData) => {
      const modelId = modelData.model?.apsViewId;
      if (modelId) {
        const cadIds = Array.from(modelToCadIdMap.get(modelId) || []);
        
        // Handle attribute fields that might not exist in ElementData interface
        if (typeof property === 'string' && ['system', 'subSystem', 'freeText1', 'freeText2', 'freeText3'].includes(property)) {
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            if ((elementData.componentCount || 0) > 0) {
              return;
            }

            const cadId = (elementData.parentCadId || elementData.cadId!)?.replace(/\.[^/.]+$/, "");
            if (cadIds.includes(cadId)) {
              const attributeValue = this.getAttributeValue(elementData, property as any);
              if (attributeValue) {
                values.add(attributeValue);
              }
            }
          });
        } else {
          const sql = `SELECT DISTINCT ${property as string} FROM ? WHERE cadId IN @(?) OR parentCadId IN @(?)`;
          const results = alasql(sql, [modelData.elementDatas, cadIds, cadIds]) as unknown[];
          results.forEach((elementData: any) => elementData[property as string] && values.add(elementData[property as string]));
        }
      }
    });

    console.log(`${property} values: `, values);
    return values;
  };

  private updateAnalyses = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    analysisType: AnalysisType) => {
    const scenarioIds = selectionManager.scenarioIds;
    if (scenarioIds.length === 0) {
      this.reset();
      return;
    }

    if (projectData) {
      this.upsertAnalysis(selectionManager, projectData, co2MaterialTypes, AnalysisType.Co2eIntensity, GroupType.Scenario);
      this.upsertAnalysis(selectionManager, projectData, co2MaterialTypes, analysisType, this.groupType);
    }
  };

  private updateSankeyAnalyses = (
    selectionManager: SelectionManager,
    projectData: ProjectData | null | undefined,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    options: SankeyOptions) => {


    if (!projectData || !co2MaterialTypes) {
      return;
    }

    const graph = {
      nodes: [],
      links: [],
    };

    const analysisData = projectAnalysis.getAnalysis(projectAnalysis.analysisType);
    if (analysisData) {
      if (options.showScenarios) {
        this.processScenarioData(projectData, selectionManager, ((scenarioData: ScenarioData) => {
          this.addUniqueNode(graph, { id: scenarioData.scenarioId, label: scenarioData.name, type: 'scenarioId' });
        }));
      }

      if (options.showModels) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          this.addUniqueNode(graph, { id: modelData.model?.modelId, label: modelData.model?.apsItemName, type: 'modelId' });
        }));
      }

      if (options.showCategories) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          modelData.categories?.forEach((categoryData: CategoryData) => {
            if (options.showEmptyNodes || _.gt(categoryData.getInScopeTotalCo2(this.totalCo2Config), 0)) {
              this.addUniqueNode(graph, { id: categoryData.cadModelCategoryId, label: categoryData.label, type: 'cadModelCategoryId' });
            }
          });
        }));
      }

      if (options.showCadMaterials) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            if (options.showEmptyNodes || _.gt(elementData.getInScopeTotalCo2(this.totalCo2Config), 0)) {
              const cadMaterial = elementData.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
              this.addUniqueNode(graph, { id: cadMaterial, label: cadMaterial, type: 'cadMaterial' });
            }
          });
        }));
      }

      if (options.showCo2MaterialTypes) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            if (options.showEmptyNodes || _.gt(elementData.getInScopeTotalCo2(this.totalCo2Config), 0)) {
              const co2MaterialTypeId = elementData.co2MaterialTypeId || EmptyGuid;
              this.addUniqueNode(graph, { id: co2MaterialTypeId, label: this.getCo2MaterialTypeName(co2MaterialTypes, co2MaterialTypeId), type: 'co2MaterialTypeId' });
            }
          });
        }));
      }


      //#region Graph Nodes Color Palette Sync

      // Pre-requisite : Ensure required analyses exist for color synchronization
      // Build analyses if they don't exist yet (needed for color maps)
      if (!this.getAnalysis(AnalysisType.CadModelCategory)) {
        this.addCategoryAnalysis(selectionManager, projectData, this.groupType);
      }
      if (!this.getAnalysis(AnalysisType.CadMaterial)) {
        this.addCadMaterialAnalysis(selectionManager, projectData, this.groupType);
      }
      if (!this.getAnalysis(AnalysisType.Co2MaterialType) && co2MaterialTypes) {
        this.addCo2MaterialTypeAnalysis(selectionManager, projectData, this.groupType, co2MaterialTypes);
      }

      // Get color maps from existing analyses
      const categoryColorMap = this.getAnalysis(AnalysisType.CadModelCategory)?.getColorMap();
      const cadMaterialColorMap = this.getAnalysis(AnalysisType.CadMaterial)?.getColorMap();
      const co2MaterialColorMap = this.getAnalysis(AnalysisType.Co2MaterialType)?.getColorMap();

      // Map of node types to color maps
      const syncedColorMaps: Record<string, Map<GroupKey, ColorDefinition> | undefined> = {
        'cadModelCategoryId': categoryColorMap,
        'cadMaterial': cadMaterialColorMap,
        'co2MaterialTypeId': co2MaterialColorMap,
      };

      // Assign synced colors to nodes that have corresponding entries in color maps 
      const nodesWithoutColor: any[] = [];
      const usedColors = new Set<string>();

      graph.nodes.forEach((node: any) => {
        const syncedColor = syncedColorMaps[node.type]?.get(node.id);
        
        if (syncedColor) {
          node.nodeColor = syncedColor.hex;
          usedColors.add(syncedColor.hex);
        } else {
          nodesWithoutColor.push(node);
        }
      });

      // Generate unique, non-overlapping colors for remaining nodes
      const uniquePalette = this.generateNonConflictingPalette(nodesWithoutColor.length, usedColors);

      // Assign unique colors to remaining nodes (Scenarios, Models, etc.)
      nodesWithoutColor.forEach((node: any, index: number) => {
        node.nodeColor = uniquePalette[index].hex;
      });

      //#endregion

      // Create Scenario to Model links
      if (options.showScenarios && options.showModels) {
        this.processModelData(projectData, selectionManager, ((scenarioData: ScenarioData, modelData: ModelData) => {
          if (options.showEmptyNodes || _.gt(modelData.getInScopeTotalCo2(this.totalCo2Config), 0)) {
            this.addLink(graph, { source: scenarioData.scenarioId, target: modelData.model?.modelId, value: modelData.getInScopeTotalCo2(this.totalCo2Config), sourceType: 'scenarioId', targetType: 'modelId', scenarioMetadata: scenarioData?.scenarioMetadata });
          }
        }));
      }

      // Create Scenario to CadModelCategory links
      if (options.showScenarios && options.showCategories && !options.showModels) {
        this.processModelData(projectData, selectionManager, ((scenarioData: ScenarioData, modelData: ModelData) => {
          const categoryMap = new Map<string, number>();
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadModelCategoryId: string = elementData.cadModelCategoryId!;

            if (categoryMap.has(cadModelCategoryId)) {
              categoryMap.set(cadModelCategoryId, (categoryMap.get(cadModelCategoryId) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            } else {
              categoryMap.set(cadModelCategoryId, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            }
          });

          categoryMap.forEach((value: number, cadModelCategoryId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: scenarioData.scenarioId, target: cadModelCategoryId, value, sourceType: 'scenarioId', targetType: 'cadModelCategoryId',scenarioMetadata:scenarioData?.scenarioMetadata });
            }
          });
        }));
      }

      // Create Scenario to CadMaterial links
      if (options.showScenarios && options.showCadMaterials && !options.showModels && !options.showCategories) {
        this.processModelData(projectData, selectionManager, ((scenarioData: ScenarioData, modelData: ModelData) => {
          const cadMaterialMap = new Map<string, number>();
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadMaterial: string = elementData.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            if (cadMaterialMap.has(cadMaterial)) {
              cadMaterialMap.set(cadMaterial, (cadMaterialMap.get(cadMaterial) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            } else {
              cadMaterialMap.set(cadMaterial, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            }
          });

          cadMaterialMap.forEach((value: number, cadMaterial: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: scenarioData.scenarioId, target: cadMaterial, value, sourceType: 'scenarioId', targetType: 'cadMaterial',scenarioMetadata:scenarioData?.scenarioMetadata });
            }
          });
        }));
      }

      // Create Scenario to Co2MaterialType links
      if (options.showScenarios && options.showCo2MaterialTypes && !options.showModels && !options.showCategories && !options.showCadMaterials) {
        this.processModelData(projectData, selectionManager, ((scenarioData: ScenarioData, modelData: ModelData) => {
          const co2MaterialTypeMap = new Map<string, number>();
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const co2MaterialTypeId: string = elementData.co2MaterialTypeId || EmptyGuid;
            if (co2MaterialTypeMap.has(co2MaterialTypeId)) {
              co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            } else {
              co2MaterialTypeMap.set(co2MaterialTypeId, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            }
          });

          co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: scenarioData.scenarioId, target: co2MaterialTypeId, value, sourceType: 'scenarioId', targetType: 'co2MaterialTypeId' ,scenarioMetadata:scenarioData?.scenarioMetadata});
            }
          });
        }));
      }

      // Create Model to CadModelCategory links
      if (options.showModels && options.showCategories) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          const categoryMap = new Map<string, number>();
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadModelCategoryId: string = elementData.cadModelCategoryId!;
            if (categoryMap.has(cadModelCategoryId)) {
              categoryMap.set(cadModelCategoryId, (categoryMap.get(cadModelCategoryId) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            } else {
              categoryMap.set(cadModelCategoryId, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            }
          });

          categoryMap.forEach((value: number, cadModelCategoryId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: modelData.model?.modelId, target: cadModelCategoryId, value, sourceType: 'modelId', targetType: 'cadModelCategoryId' ,scenarioMetadata:_scenarioData?.scenarioMetadata });
            }
          });
        }));
      }

      // Create Model to CadMaterial links
      if (options.showModels && options.showCadMaterials && !options.showCategories) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          const cadMaterialMap = new Map<string, number>();
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadMaterial: string = elementData.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            if (cadMaterialMap.has(cadMaterial)) {
              cadMaterialMap.set(cadMaterial, (cadMaterialMap.get(cadMaterial) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            } else {
              cadMaterialMap.set(cadMaterial, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            }
          });

          cadMaterialMap.forEach((value: number, cadMaterial: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: modelData.model?.modelId, target: cadMaterial, value, sourceType: 'modelId', targetType: 'cadMaterial',scenarioMetadata:_scenarioData?.scenarioMetadata });
            }
          });
        }));
      }

      // Create Model to Co2MaterialType links
      if (options.showModels && options.showCo2MaterialTypes && !options.showCadMaterials && !options.showCategories) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          const co2MaterialTypeMap = new Map<string, number>();
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const co2MaterialTypeId: string = elementData.co2MaterialTypeId || EmptyGuid;
            if (co2MaterialTypeMap.has(co2MaterialTypeId)) {
              co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            } else {
              co2MaterialTypeMap.set(co2MaterialTypeId, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
            }
          });

          co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
            if (options.showEmptyNodes || _.gt(value, 0)) {
              this.addLink(graph, { source: modelData.model?.modelId, target: co2MaterialTypeId, value, sourceType: 'modelId', targetType: 'co2MaterialTypeId',scenarioMetadata:_scenarioData?.scenarioMetadata });
            }
          });
        }));
      }

      // Create CadModelCategory to CadMaterial links
      if (options.showCategories && options.showCadMaterials) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          const cadModelCategoryMap = new Map<string, Map<string, number>>(); // cadModelCategoryId -> cadMaterial -> totalCo2
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadModelCategoryId: string = elementData.cadModelCategoryId!;
            const cadMaterial: string = elementData.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            if (cadModelCategoryMap.has(cadModelCategoryId)) {
              const cadMaterialMap = cadModelCategoryMap.get(cadModelCategoryId);
              if (cadMaterialMap) {
                if (cadMaterialMap.has(cadMaterial)) {
                  cadMaterialMap.set(cadMaterial, (cadMaterialMap.get(cadMaterial) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
                } else {
                  cadMaterialMap.set(cadMaterial, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
                }
              }
            } else {
              const cadMaterialMap = new Map<string, number>();
              cadMaterialMap.set(cadMaterial, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
              cadModelCategoryMap.set(cadModelCategoryId, cadMaterialMap);
            }
          });

          cadModelCategoryMap.forEach((cadMaterialMap: Map<string, number>, cadModelCategoryId: string) => {
            cadMaterialMap.forEach((value: number, cadMaterial: string) => {
              if (options.showEmptyNodes || _.gt(value, 0)) {
                this.addLink(graph, { source: cadModelCategoryId, target: cadMaterial, value, sourceType: 'cadModelCategoryId', targetType: 'cadMaterial' ,scenarioMetadata:_scenarioData?.scenarioMetadata  });
              }
            });
          });
        }));
      }

      // Create CadModelCategory to Co2MaterialType links
      if (options.showCategories && options.showCo2MaterialTypes && !options.showCadMaterials) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          const cadModelCategoryMap = new Map<string, Map<string, number>>(); // cadModelCategoryId -> co2MaterialTypeId -> totalCo2
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadModelCategoryId: string = elementData.cadModelCategoryId!;
            const co2MaterialTypeId: string = elementData.co2MaterialTypeId || EmptyGuid;
            if (cadModelCategoryMap.has(cadModelCategoryId)) {
              const co2MaterialTypeMap = cadModelCategoryMap.get(cadModelCategoryId);
              if (co2MaterialTypeMap) {
                if (co2MaterialTypeMap.has(co2MaterialTypeId)) {
                  co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
                } else {
                  co2MaterialTypeMap.set(co2MaterialTypeId, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
                }
              }
            } else {
              const co2MaterialTypeMap = new Map<string, number>();
              co2MaterialTypeMap.set(co2MaterialTypeId, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
              cadModelCategoryMap.set(cadModelCategoryId, co2MaterialTypeMap);
            }
          });

          cadModelCategoryMap.forEach((co2MaterialTypeMap: Map<string, number>, cadModelCategoryId: string) => {
            co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
              if (options.showEmptyNodes || _.gt(value, 0)) {
                this.addLink(graph, { source: cadModelCategoryId, target: co2MaterialTypeId, value, sourceType: 'cadModelCategoryId', targetType: 'co2MaterialTypeId',scenarioMetadata:_scenarioData?.scenarioMetadata });
              }
            });
          });
        }));
      }

      // Create CadMaterial to Co2MaterialType links
      if (options.showCadMaterials && options.showCo2MaterialTypes) {
        this.processModelData(projectData, selectionManager, ((_scenarioData: ScenarioData, modelData: ModelData) => {
          const cadMaterialMap = new Map<string, Map<string, number>>(); // cadMaterialId -> co2MaterialTypeId -> totalCo2
          modelData.elementDatas?.forEach((elementData: ElementData) => {
            const cadMaterial: string = elementData.cadMaterial || i18n.t('analysis.rumps-title.cad-material');
            const co2MaterialTypeId: string = elementData.co2MaterialTypeId || EmptyGuid;
            if (cadMaterialMap.has(cadMaterial)) {
              const co2MaterialTypeMap = cadMaterialMap.get(cadMaterial);
              if (co2MaterialTypeMap) {
                if (co2MaterialTypeMap.has(co2MaterialTypeId)) {
                  co2MaterialTypeMap.set(co2MaterialTypeId, (co2MaterialTypeMap.get(co2MaterialTypeId) || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
                } else {
                  co2MaterialTypeMap.set(co2MaterialTypeId, (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0));
                }
              }
            } else {
              const co2MaterialTypeMap = new Map<string, number>();
              co2MaterialTypeMap.set(co2MaterialTypeId, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
              cadMaterialMap.set(cadMaterial, co2MaterialTypeMap);
            }
          });

          cadMaterialMap.forEach((co2MaterialTypeMap: Map<string, number>, cadMaterial: string) => {
            co2MaterialTypeMap.forEach((value: number, co2MaterialTypeId: string) => {
              if (options.showEmptyNodes || _.gt(value, 0)) {
                this.addLink(graph, { source: cadMaterial, target: co2MaterialTypeId, value, sourceType: 'cadMaterial', targetType: 'co2MaterialTypeId',scenarioMetadata:_scenarioData?.scenarioMetadata });
              }
            });
          });
        }));
      }
    }

    console.log('SankeyData: ', graph);

    // TODO: add SankyData to analyses: Map<AnalysisType, AnalysisData> = new Map();
    this.analyses.set(AnalysisType.Sankey, new SankeyData(graph) as any);
  };

  private addLink = (graph: any, link: any) => {
    // Validate that both source and target nodes exist before adding the link
    const sourceExists = graph.nodes.some((node: any) => node.id === link.source);
    const targetExists = graph.nodes.some((node: any) => node.id === link.target);
    
    if (!sourceExists) {
      console.warn(`Sankey link source node not found: ${link.source}`, link);
      return;
    }
    
    if (!targetExists) {
      console.warn(`Sankey link target node not found: ${link.target}`, link);
      return;
    }
    
    graph.links.push(link);
  };

  private addUniqueNode = (graph: any, node: any,) => {
    // Validate that the node has a valid ID before adding
    if (!node || !node.id) {
      console.warn('Sankey node missing ID:', node);
      return;
    }
    
    if (!graph.nodes.find((n: any) => n.id === node.id)) {
      graph.nodes.push(node);
    }
  };
  
  /*
   * Generates a palette of colors that are not already used in the given set of colors
   * Effective usage in SankeyChart to sync the color pallete from different 
   * analysisTypes - CadModelCategory, CadMaterial, Co2MaterialType
   */
  private generateNonConflictingPalette = (count: number, usedColors: Set<string>): ColorDefinition[] => {
    if (count === 0) {
      return [];
    }    

    // Generate extra colors to account for filtering out used ones
    const basePalette = DataVisualization.Colors.calcPalette(
      DataVisualization.Colors.MaterialsPalette, 
      count + usedColors.size + 10  // Extra buffer
    );
    
    // Filter out colors that are already used
    const palette: ColorDefinition[] = basePalette
        .filter((color: ColorDefinition) => !usedColors.has(color.hex))
        .slice(0, count);
    
    // If left with less colors than needed, generate colors directly
    while (palette.length < count) {
      const newColor = DataVisualization.Colors.calcNextColor([...palette, ...basePalette]);
      if (!usedColors.has(newColor.hex)) {
        palette.push(newColor);
      }
    }

    return palette;
  };

  private hasSelection = (analysisType: AnalysisType) => {
    return this.selections.has(analysisType);
  };

  private hasAnalysisData = (analysisType: AnalysisType, groupType: GroupType) => {
    return this.analyses.get(analysisType)?.groupType === groupType;
  };

  private upsertAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    co2MaterialTypes: Co2MaterialType[] | null | undefined,
    analysisType: AnalysisType,
    groupType: GroupType) => {
    switch (analysisType) {
      case AnalysisType.CadModelCategory:
        if (!this.hasAnalysisData(AnalysisType.CadModelCategory, groupType)) {
          this.addCategoryAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.CadMaterial:
        if (!this.hasAnalysisData(AnalysisType.CadMaterial, groupType)) {
          this.addCadMaterialAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.CadMaterialCategory:
        if (!this.hasAnalysisData(AnalysisType.CadMaterialCategory, groupType)) {
          this.addCadMaterialCategoryAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.Co2MaterialType:
        if (!this.hasAnalysisData(AnalysisType.Co2MaterialType, groupType)) {
          this.addCo2MaterialTypeAnalysis(selectionManager, projectData, groupType, co2MaterialTypes);
        }
        break;
      case AnalysisType.Co2MaterialCategory:
        if (!this.hasAnalysisData(AnalysisType.Co2MaterialCategory, groupType)) {
          this.addCo2MaterialCategoryAnalysis(selectionManager, projectData, groupType, co2MaterialTypes);
        }
        break;
      case AnalysisType.ModelGroup:
        if (!this.hasAnalysisData(AnalysisType.ModelGroup, groupType)) {
          this.addModelGroupAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.Co2eIntensity:
        if (!this.hasAnalysisData(AnalysisType.Co2eIntensity, groupType)) {
          this.updateCo2eIntensityAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.System:
        if (!this.hasAnalysisData(AnalysisType.System, groupType)) {
          this.addSystemAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.SubSystem:
        if (!this.hasAnalysisData(AnalysisType.SubSystem, groupType)) {
          this.addSubSystemAnalysis(selectionManager, projectData, groupType);
        }
        break;
      case AnalysisType.Attribute1:
        if (!this.hasAnalysisData(AnalysisType.Attribute1, groupType)) {
          this.addAttributeAnalysis(selectionManager, projectData, groupType, 'freeText1');
        }
        break;
      case AnalysisType.Attribute2:
        if (!this.hasAnalysisData(AnalysisType.Attribute2, groupType)) {
          this.addAttributeAnalysis(selectionManager, projectData, groupType, 'freeText2');
        }
        break;
      case AnalysisType.Attribute3:
        if (!this.hasAnalysisData(AnalysisType.Attribute3, groupType)) {
          this.addAttributeAnalysis(selectionManager, projectData, groupType, 'freeText3');
        }
        break;
      default:
        break;
    }
  };

  private updateCo2eIntensityAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = this.analyses.get(AnalysisType.Co2eIntensity) || new AnalysisData(AnalysisType.Co2eIntensity, groupType);
    this.analyses.set(AnalysisType.Co2eIntensity, analysisData);

    const colorPalette = DataVisualization.Colors.TotalCo2Palette;

    // Loop through the scenarios and models to aggregate the TotalCO2 data
    projectData.scenarioDatas?.forEach((scenarioData: ScenarioData, scenarioIndex: number) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        // Create a ScenarioAnalysis for each scenario
        const scenarioAnalysis = analysisData.scenarioAnalyses.get(scenarioId) || new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife);
        scenarioAnalysis.groupData.length = 0;
        scenarioAnalysis.groupColorMap = new Map(Array.from(Array(TOTALCO2_BUCKET_SIZE).keys()).map((key, index) => [key, colorPalette[index]]));
        analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

        let groupAnalysis: GroupAnalysis;
        let buckets: Buckets;
        let numZeroTotalCo2Values = 0;
        const items: AnalysisItem[] = [];
        const ecValues: number[] = [];

        if (groupType === GroupType.Scenario) {
          groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, this.analysisType, groupType);
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        if (groupType === GroupType.Scenario) {
          // Collect all of the totalCo2 values for the selected models
          numZeroTotalCo2Values = 0;
          scenarioData.modelDatas?.forEach((modelData: ModelData) => {
            if (this.isModelSelected(selectionManager, scenarioId, modelData)) {

              modelData.elementDatas?.forEach((elementData: ElementData) => {
                // Skip elements that have components
                if ((elementData.componentCount || 0) > 0) {
                  return;
                }

                const totalCo2 = elementData.getInScopeTotalCo2(this.totalCo2Config);
                if (_.gt(totalCo2, 0)) {
                  ecValues.push(totalCo2!);
                } else {
                  numZeroTotalCo2Values++;
                }
              });
            }
          });

          // TODO: create a bucket class to encapsulate the bucket logic
          buckets = this.createBuckets(ecValues, TOTALCO2_BUCKET_SIZE);
        }

        scenarioData.modelDatas?.forEach((modelData: ModelData, modelIndex: number) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {

            if (groupType === GroupType.Model) {
              groupAnalysis = new GroupAnalysis(modelData.model!.modelId!, modelIndex, modelData.model!.apsItemName!, AnalysisType.Co2eIntensity, groupType);
              scenarioAnalysis.groupData.push(groupAnalysis);
              items.length = 0;

              // Collect all of the totalCo2 values for the selected models
              ecValues.length = 0;
              numZeroTotalCo2Values = 0;
              modelData.elementDatas?.forEach((elementData: ElementData) => {
                // Skip elements that have components
                if ((elementData.componentCount || 0) > 0) {
                  return;
                }

                const totalCo2 = elementData.getInScopeTotalCo2(this.totalCo2Config);
                if (_.gt(totalCo2, 0)) {
                  ecValues.push(totalCo2!);
                } else {
                  numZeroTotalCo2Values++;
                }
              });

              // TODO: create a bucket class to encapsulate the bucket logic
              buckets = this.createBuckets(ecValues, TOTALCO2_BUCKET_SIZE);
            }

            const modelIdentifier = (modelData.model?.apsViewId || modelData.model?.modelId) as string;
            const modelCadIdMap = new Map<GroupKey, CadIds>();
            Array.from(Array(TOTALCO2_BUCKET_SIZE).keys()).forEach((bucket: number) => modelCadIdMap.set(bucket, []));
            scenarioAnalysis.modelGroupCadIdsMap.set(modelIdentifier, modelCadIdMap);

            modelData.elementDatas?.forEach((elementData: ElementData) => {
              // Skip elements that have components
              if ((elementData.componentCount || 0) > 0) {
                return;
              }

              const totalCo2 = elementData.getInScopeTotalCo2(this.totalCo2Config);

              if (_.gt(totalCo2, 0)) {
                const ecValue = totalCo2;
                const bucketIndex = buckets.data.findIndex((bucket) => bucket.includes(ecValue!));
                elementData.bucketIndex = bucketIndex;
                elementData.hexColor = scenarioAnalysis.groupColorMap.get(TOTALCO2_BUCKET_SIZE - bucketIndex - 1)?.hex;
                const cadId = (elementData.parentCadId || elementData.cadId!)?.replace(/\.[^/.]+$/, ""); // for components strip off the suffix 123.1 => 123
                // TODO: this should be a Set not Array
                modelCadIdMap.get(bucketIndex)?.push(cadId!);

                // Update the parent element color when we have a component indicated by the parentCadId
                // This is N2 complexity, boooooo!
                if (elementData.parentCadId) {
                  const parentElement = modelData.elementDatas?.find((parentElement: ElementData) => parentElement.cadId === cadId);
                  if (parentElement) {
                    const curColorIndex = DataVisualization.Colors.TotalCo2Palette.findIndex((color: ColorDefinition) => color.hex === parentElement?.hexColor);
                    const newColorIndex = DataVisualization.Colors.TotalCo2Palette.findIndex((color: ColorDefinition) => color.hex === elementData.hexColor);
                    if (curColorIndex === -1 || newColorIndex < curColorIndex) {
                      parentElement!.hexColor = DataVisualization.Colors.TotalCo2Palette[newColorIndex].hex;
                    }
                  }
                }
              } else {
                const bucketIndex = 0;
                elementData.bucketIndex = bucketIndex;
                elementData.hexColor = scenarioAnalysis.groupColorMap.get(TOTALCO2_BUCKET_SIZE - bucketIndex - 1)?.hex;
                const cadId = (elementData.parentCadId || elementData.cadId!)?.replace(/\.[^/.]+$/, ""); // for components strip off the suffix 123.1 => 123
                // TODO: this should be a Set not Array
                modelCadIdMap.get(bucketIndex)?.push(cadId!);
              }
            });

            if (groupType === GroupType.Model) {
              buckets.data.forEach((ecValues: number[], index: number) => {
                const item = new AnalysisItem(index, "");
                item.elementCount = index === 0 ? numZeroTotalCo2Values : ecValues.length;
                item.co2eTotal = _.sum(ecValues); // TODO: co2eTotal should be the whole number if provided to a DataGrid
                item.minCo2e = _.min(ecValues) || 0;
                item.maxCo2e = _.max(ecValues) || 0;

                if (item.elementCount === 0) {
                  item.name = "0";
                } else if (item.minCo2e === item.maxCo2e) {
                  item.name = `${i18n.t("number", { value: item.minCo2e })} (${i18n.t("number", { value: item.co2eTotal })} tCO₂e)`; // TODO: translate tCO₂e
                } else {
                  item.name = `${i18n.t("number", { value: item.minCo2e })} - ${i18n.t("number", { value: item.maxCo2e })} (${i18n.t("number", {
                    val: item.co2eTotal,
                  })} tCO₂e)`; // TODO: tCO₂e should be translated here
                }
                items.push(item);
              });

              items.forEach((item: AnalysisItem) => groupAnalysis.items.set(item.groupKey, item));
            }

            if (groupType === GroupType.Scenario) {
              buckets.data.forEach((ecValues: number[], index: number) => {
                const item = new AnalysisItem(index, "");
                item.elementCount = index === 0 ? numZeroTotalCo2Values : ecValues.length;
                item.co2eTotal = _.sum(ecValues); // TODO: co2eTotal should be the whole number if provided to a DataGrid
                item.minCo2e = _.min(ecValues) || 0;
                item.maxCo2e = _.max(ecValues) || 0;

                if (item.elementCount === 0) {
                  item.name = "0";
                } else if (item.minCo2e === item.maxCo2e) {
                  item.name = `${i18n.t("number", { value: item.minCo2e })} (${i18n.t("number", { value: item.co2eTotal })} tCO₂e)`; // TODO: translate tCO₂e
                } else {
                  item.name = `${i18n.t("number", { value: item.minCo2e })} - ${i18n.t("number", { value: item.maxCo2e })} (${i18n.t("number", {
                    val: item.co2eTotal,
                  })} tCO₂e)`; // TODO: tCO₂e should be translated here
                }
                items.push(item);
              });

              items.forEach((item: AnalysisItem) => groupAnalysis.items.set(item.groupKey, item));

              scenarioAnalysis.co2eIntensity = groupAnalysis;
            }
          }
        });
      }
    });
  };

  private addCategoryAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = new AnalysisData(AnalysisType.CadModelCategory, groupType);
    this.analyses.set(AnalysisType.CadModelCategory, analysisData);

    // Build a set of all categories
    const cadModelCategoryIds = this.getCategoryGroupKeys(projectData, scenarioIds, selectionManager);
    const cadModelCategories = this.getCadModelCategories(projectData, scenarioIds, selectionManager); // Map of cadModelCategoryId -> name
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, cadModelCategoryIds.size);

    // Loop through the scenarios and models to aggregate the category data
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData, scenarioIndex: number) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        // Create a ScenarioAnalysis for each scenario
        const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife!);
        scenarioAnalysis.groupColorMap = new Map(Array.from(cadModelCategoryIds).map((key, index) => [key, colorPalette[index]]));
        analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

        let groupAnalysis: GroupAnalysis;
        const items: AnalysisItem[] = [];

        if (groupType === GroupType.Scenario) {
          groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, AnalysisType.CadModelCategory, groupType);
          cadModelCategoryIds.forEach((cadModelCategoryId: string) => items.push(new AnalysisItem(cadModelCategoryId, this.getCadModelCategoryName(cadModelCategories, cadModelCategoryId))));
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        scenarioData.modelDatas?.forEach((modelData: ModelData, modelIndex: number) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {

            if (groupType === GroupType.Model) {
              groupAnalysis = new GroupAnalysis(modelData.model!.modelId!, modelIndex, modelData.model!.apsItemName!, AnalysisType.CadModelCategory, groupType);
              items.length = 0;
              cadModelCategoryIds.forEach((cadModelCategoryId: string) => items.push(new AnalysisItem(cadModelCategoryId, this.getCadModelCategoryName(cadModelCategories, cadModelCategoryId))));
              scenarioAnalysis.groupData.push(groupAnalysis);
            }

            // Aggregate the category data for each model in the scenario
            modelData.categories?.forEach((categoryData: CategoryData) => {
              const item = items.find((item) => item.groupKey === categoryData.cadModelCategoryId);

              if (item) {
                item.elementCount = (item.elementCount || 0) + (categoryData.elementCount || 0);
                item.lengthTotal = (item.lengthTotal || 0) + (categoryData.lengthTotal || 0);
                item.areaTotal = (item.areaTotal || 0) + (categoryData.areaTotal || 0);
                item.volumeTotal = (item.volumeTotal || 0) + (categoryData.volumeTotal || 0);
                item.co2eTotal = (item.co2eTotal || 0) + (categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                //TODO: Add min n max co2e
                item.minCo2e = Math.min(item.minCo2e, categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                item.maxCo2e = Math.max(item.maxCo2e, categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0);
              } else {
                const item = new AnalysisItem(categoryData.cadModelCategoryId!, categoryData.label!);
                item.elementCount = categoryData.elementCount || 0;
                item.lengthTotal = categoryData.lengthTotal || 0;
                item.areaTotal = categoryData.areaTotal || 0;
                item.volumeTotal = categoryData.volumeTotal || 0;
                item.co2eTotal = categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0;
                item.minCo2e = categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0;
                item.maxCo2e = categoryData.getInScopeTotalCo2(this.totalCo2Config) || 0;
                items.push(item);
              }
            });

            if (groupType === GroupType.Model) {
              items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
            }
          }
        });

        if (groupType === GroupType.Scenario) {
          items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
        }
      }
    });
  };

  private addCadMaterialAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = new AnalysisData(AnalysisType.CadMaterial, groupType);
    this.analyses.set(AnalysisType.CadMaterial, analysisData);

    // Build a set of all CAD materials
    const cadMaterials = this.getCadMaterialGroupKeys(projectData, scenarioIds, selectionManager);
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, cadMaterials.size);

    // Loop through the scenarios and models to aggregate the category data
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData, scenarioIndex: number) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        // Create a ScenarioAnalysis for each scenario
        const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife);
        scenarioAnalysis.groupColorMap = new Map(Array.from(cadMaterials).map((key, index) => [key, colorPalette[index]]));
        analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

        let groupAnalysis: GroupAnalysis;
        const items: AnalysisItem[] = [];

        if (groupType === GroupType.Scenario) {
          groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, AnalysisType.CadMaterial, groupType);
          cadMaterials.forEach((cadMaterial: string) => items.push(new AnalysisItem(cadMaterial, this.getCadMaterialName(cadMaterial))));
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        scenarioData.modelDatas?.forEach((modelData: ModelData, modelIndex: number) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {

            if (groupType === GroupType.Model) {
              groupAnalysis = new GroupAnalysis(modelData.model!.modelId!, modelIndex, modelData.model!.apsItemName!, AnalysisType.CadMaterial, groupType);
              items.length = 0;
              cadMaterials.forEach((cadMaterial: string) => items.push(new AnalysisItem(cadMaterial, this.getCadMaterialName(cadMaterial))));
              scenarioAnalysis.groupData.push(groupAnalysis);
            }

            const modelIdentifier = (modelData.model?.apsViewId || modelData.model?.modelId) as string;
            const groupCadIdsMap = new Map(Array.from(cadMaterials).map((key) => [key, [] as CadIds]));
            scenarioAnalysis.modelGroupCadIdsMap.set(modelIdentifier, groupCadIdsMap);

            const rumpItem = items.find((item) => item.groupKey === i18n.t('analysis.rumps-title.cad-material'));
            // Aggregate the category data for each model in the scenario
            modelData.elementDatas?.forEach((elementData: ElementData) => {
              // Skip elements that have components
              if ((elementData.componentCount || 0) > 0) {
                return;
              }

              const cadMaterialGroupKey = this.getCadMaterialGroupKey(elementData);
              const item = cadMaterialGroupKey ? items.find((analysisItem) => analysisItem.groupKey === cadMaterialGroupKey) : rumpItem;
              if (item) {
                item.elementCount = item.elementCount + 1;
                //item.lengthTotal = (item.lengthTotal || 0) + (elementData.lengthTotal || 0);
                //item.areaTotal = (item.areaTotal || 0) + (elementData.areaTotal || 0);
                //item.volumeTotal = (item.volumeTotal || 0) + (elementData.volumeTotal || 0);
                item.co2eTotal = (item.co2eTotal || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                //TODO: Add min n max co2e
                item.minCo2e = Math.min(item.minCo2e, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                item.maxCo2e = Math.max(item.maxCo2e, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);

                const cadId = (elementData.parentCadId || elementData.cadId!)?.replace(/\.[^/.]+$/, ""); // for components strip off the suffix 123.1 => 123;
                // TODO: this should be a Set not Array
                groupCadIdsMap.get(item.groupKey as string)?.push(cadId);
              } else {
                console.error(`CadMaterial not found for group key: ${cadMaterialGroupKey ?? elementData.cadMaterial}`);
              }
            });

            if (groupType === GroupType.Model) {
              items.forEach((item) => {
                groupAnalysis.items.set(item.groupKey, item);
              });
            }
          }
        });

        if (groupType === GroupType.Scenario) {
          items.forEach((item) => {
            groupAnalysis.items.set(item.groupKey, item);
          });
        }
      }
    });
  };

  private addCadMaterialCategoryAnalysis = (
    _selectionManager: SelectionManager,
    _projectData: ProjectData,
    _groupType: GroupType) => {
  };

  private addCo2MaterialTypeAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType,
    co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
    const scenarioIds = selectionManager.scenarioIds;

    const analysisData: AnalysisData = new AnalysisData(AnalysisType.Co2MaterialType, groupType);
    this.analyses.set(AnalysisType.Co2MaterialType, analysisData);

    // Build a set of all co2MaterialTypes
    const co2MaterialTypeIds = this.getCo2MaterialTypeGroupKeys(projectData, scenarioIds, selectionManager);
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, co2MaterialTypeIds.size);

    // Loop through the scenarios and models to aggregate the category data
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData, scenarioIndex: number) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        // Create a ScenarioAnalysis for each scenario
        const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType, scenarioData.scenarioMetadata?.totalFloorArea, scenarioData.scenarioMetadata?.serviceLife);
        analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);
        scenarioAnalysis.groupColorMap = new Map(Array.from(co2MaterialTypeIds).map((key, index) => [key, colorPalette[index]]));

        let groupAnalysis: GroupAnalysis;
        const items: AnalysisItem[] = [];

        if (groupType === GroupType.Scenario) {
          groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, AnalysisType.Co2MaterialType, groupType);
          co2MaterialTypeIds.forEach((co2MaterialTypeId: string) => items.push(new AnalysisItem(co2MaterialTypeId, this.getCo2MaterialTypeName(co2MaterialTypes, co2MaterialTypeId))));
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        scenarioData.modelDatas?.forEach((modelData: ModelData, modelIndex: number) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {

            if (groupType === GroupType.Model) {
              groupAnalysis = new GroupAnalysis(modelData.model!.modelId!, modelIndex, modelData.model!.apsItemName!, AnalysisType.Co2MaterialType, groupType);
              items.length = 0;
              co2MaterialTypeIds.forEach((co2MaterialTypeId: string) => items.push(new AnalysisItem(co2MaterialTypeId, this.getCo2MaterialTypeName(co2MaterialTypes, co2MaterialTypeId))));
              scenarioAnalysis.groupData.push(groupAnalysis);
            }

            const modelIdentifier = (modelData.model?.apsViewId || modelData.model?.modelId) as string;
            const groupCadIdsMap = new Map(Array.from(co2MaterialTypeIds).map((key) => [key, [] as CadIds]));
            scenarioAnalysis.modelGroupCadIdsMap.set(modelIdentifier, groupCadIdsMap);

            const rumpItem = items.find((item) => item.groupKey === EmptyGuid);
            // Aggregate the category data for each model in the scenario
            modelData.elementDatas?.forEach((elementData: ElementData) => {
              // Skip elements that have components
              if ((elementData.componentCount || 0) > 0) {
                return;
              }

              const co2MaterialTypeGroupKey = this.getCo2MaterialTypeGroupKey(elementData);
              const item = co2MaterialTypeGroupKey
                ? items.find((analysisItem) => analysisItem.groupKey === co2MaterialTypeGroupKey)
                : rumpItem;
              if (item) {
                item.elementCount = item.elementCount + 1;
                //item.lengthTotal = (item.lengthTotal || 0) + (elementData.lengthTotal || 0);
                //item.areaTotal = (item.areaTotal || 0) + (elementData.areaTotal || 0);
                //item.volumeTotal = (item.volumeTotal || 0) + (elementData.volumeTotal || 0);
                item.co2eTotal = (item.co2eTotal || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                //TODO: Add min n max co2e
                item.minCo2e = Math.min(item.minCo2e, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                item.maxCo2e = Math.max(item.maxCo2e, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);

                const cadId = (elementData.parentCadId || elementData.cadId!)?.replace(/\.[^/.]+$/, ""); // for components strip off the suffix 123.1 => 123;
                // TODO: this should be a Set not Array
                groupCadIdsMap.get(item.groupKey as string)?.push(cadId);
              } else {
                console.error(`Co2MaterialType not found for group key: ${co2MaterialTypeGroupKey ?? elementData.co2MaterialTypeId}`);
              }
            });

            if (groupType === GroupType.Model) {
              items.forEach((item) => {
                groupAnalysis.items.set(item.groupKey, item);
              });
            }
          }
        });

        if (groupType === GroupType.Scenario) {
          items.forEach((item) => {
            groupAnalysis.items.set(item.groupKey, item);
          });
        }
      }
    });
  };

  private addCo2MaterialCategoryAnalysis = (
    _selectionManager: SelectionManager,
    _projectData: ProjectData,
    _groupType: GroupType,
    _co2MaterialTypes: Co2MaterialType[] | null | undefined) => {
  };

  private addModelGroupAnalysis = (_selectionManager: SelectionManager, _projectData: ProjectData, _groupType: GroupType) => {
  };

  private getCadModelCategoryName = (cadModelCategories: Map<string, string>, cadModelCategoryId: string) => {
    if (cadModelCategoryId === EmptyGuid) {
      return i18n.t('analysis.rumps-title.co2-material-type');
    }

    const cadModelCategoryName = cadModelCategories.get(cadModelCategoryId);
    return cadModelCategoryName || cadModelCategoryId;
  };

  private getCadMaterialName = (cadMaterial: string) => {
    if (cadMaterial === EstimatedRebarCadMaterial) {
      return i18n.t("analysis.virtual-title.estimated-rebar", { defaultValue: "Estimated Rebar" });
    }

    if (cadMaterial === EstimatedNonModelledCadMaterial) {
      return i18n.t("analysis.virtual-title.estimated-non-modelled", { defaultValue: "Estimated Non-Modelled" });
    }

    return cadMaterial;
  };

  private getCo2MaterialTypeName = (co2MaterialTypes: Co2MaterialType[] | null | undefined, co2MaterialTypeId: string) => {
    if (co2MaterialTypeId === EstimatedRebarCo2MaterialTypeId) {
      return i18n.t("analysis.virtual-title.estimated-rebar", { defaultValue: "Estimated Rebar" });
    }

    if (co2MaterialTypeId === EstimatedNonModelledCo2MaterialTypeId) {
      return i18n.t("analysis.virtual-title.estimated-non-modelled", { defaultValue: "Estimated Non-Modelled" });
    }

    if (co2MaterialTypeId === EmptyGuid) {
      return i18n.t('analysis.rumps-title.co2-material-type');
    }

    const co2MaterialType = co2MaterialTypes?.find((co2MaterialType: Co2MaterialType) => co2MaterialType.co2MaterialTypeId === co2MaterialTypeId);
    return co2MaterialType?.name || co2MaterialTypeId;
  };

  private getCadModelCategories = (
    projectData: ProjectData,
    scenarioIds: string[],
    selectionManager: SelectionManager
  ) => {
    const cadModelCategories = new Map<string, string>();
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        scenarioData.modelDatas?.forEach((modelData: ModelData) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {
            modelData.categories?.forEach((categoryData: CategoryData) => {
              cadModelCategories.set(categoryData.cadModelCategoryId!, categoryData.label!);
            });
          };
        });
      }
    });
    return cadModelCategories;
  };

  private getCategoryGroupKeys = (
    projectData: ProjectData,
    scenarioIds: string[],
    selectionManager: SelectionManager) => {
    const cadModelCategoryIds = new Set<string>();
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        scenarioData.modelDatas?.forEach((modelData: ModelData) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {
            modelData.categories?.forEach((categoryData: CategoryData) => {
              cadModelCategoryIds.add(categoryData.cadModelCategoryId!);
            });
          };
        });
      }
    });
    return cadModelCategoryIds;
  };

  private getCadMaterialGroupKeys = (
    projectData: ProjectData,
    scenarioIds: string[],
    selectionManager: SelectionManager) => {
    const cadMaterials = new Set<string>();
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        scenarioData.modelDatas?.forEach((modelData: ModelData) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {
            modelData.elementDatas?.forEach((elementData: ElementData) => {
              // Supplemental types must always be shown as virtual CAD materials.
              if (elementData.type === MaterialType.RebarSupplemental) {
                cadMaterials.add(EstimatedRebarCadMaterial);
                return;
              }

              if (elementData.type === MaterialType.NonModelledSupplemental) {
                cadMaterials.add(EstimatedNonModelledCadMaterial);
                return;
              }

              if (elementData.cadMaterial) {
                cadMaterials.add(elementData.cadMaterial);
                return;
              }
            });
          };
        });
      }
    });
    cadMaterials.add(i18n.t('analysis.rumps-title.cad-material'));
    return cadMaterials;
  };

  private getCo2MaterialTypeGroupKeys = (
    projectData: ProjectData,
    scenarioIds: string[],
    selectionManager: SelectionManager) => {
    const co2MaterialTypes = new Set<string>();
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        scenarioData.modelDatas?.forEach((modelData: ModelData) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {
            modelData.elementDatas?.forEach((elementData: ElementData) => {
              if (elementData.co2MaterialTypeId) {
                co2MaterialTypes.add(elementData.co2MaterialTypeId);
                return;
              }
            });
          };
        });
      }
    });
    co2MaterialTypes.add(EmptyGuid);
    return co2MaterialTypes;
  };

  private processScenarioData = (
    projectData: ProjectData,
    selectionManager: SelectionManager,
    callback: (scenarioData: ScenarioData) => void) => {
    const scenarioIds = selectionManager.scenarioIds;

    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        callback(scenarioData);
      }
    });
  };

  // Helper method to check if a model is selected for a given scenario,
  // handles both viewer and non-viewer models (Revit, JSON)
  private isModelSelected = (
    selectionManager: SelectionManager,
    scenarioId: string,
    modelData: ModelData): boolean => {
    const identifier = modelData.model?.apsViewId || modelData.model?.modelId;
    if (!identifier) return false;
    return selectionManager.getSelectedModelUrns(scenarioId).includes(identifier);
  };

  private processModelData = (
    projectData: ProjectData,
    selectionManager: SelectionManager,
    callback: (scenarioData: ScenarioData, modelData: ModelData) => void) => {
    const scenarioIds = selectionManager.scenarioIds;

    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        scenarioData.modelDatas?.forEach((modelData: ModelData) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {
            callback(scenarioData, modelData);
          }
        });
      }
    });
  };

  private createBuckets = (ecValues: number[], numBuckets: number) => {
    // We reserve the 
    const buckets = bucketize(ecValues, numBuckets - 1);
    buckets.data.unshift([0]);
    return buckets;
  };

  private addSystemAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType) => {
    this.addAttributeAnalysis(selectionManager, projectData, groupType, 'system');
  };

  private addSubSystemAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType) => {
    this.addAttributeAnalysis(selectionManager, projectData, groupType, 'subSystem');
  };

  private addAttributeAnalysis = (
    selectionManager: SelectionManager,
    projectData: ProjectData,
    groupType: GroupType,
    attributeField: 'system' | 'subSystem' | 'freeText1' | 'freeText2' | 'freeText3') => {
    const scenarioIds = selectionManager.scenarioIds;
    if (scenarioIds.length === 0) {
      return;
    }

    const analysisType = this.getAnalysisTypeFromAttributeField(attributeField);
    console.log('Creating attribute analysis:', { attributeField, analysisType, groupType });
    const analysisData: AnalysisData = new AnalysisData(analysisType, groupType);
    this.analyses.set(analysisType, analysisData);

    // Get all unique attribute values
    const attributeValues = this.getAllAttributeValues(projectData, scenarioIds, selectionManager, attributeField);
    console.log('Found attribute values:', { attributeField, count: attributeValues.size, values: Array.from(attributeValues) });
    const colorPalette = DataVisualization.Colors.calcPalette(DataVisualization.Colors.MaterialsPalette, attributeValues.size);

    // Process each scenario
    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData, scenarioIndex: number) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        const scenarioAnalysis = new ScenarioAnalysis(scenarioId, scenarioIndex, scenarioData.name!, groupType);
        scenarioAnalysis.groupColorMap = new Map(Array.from(attributeValues).map((key, index) => [key, colorPalette[index]]));
        analysisData.scenarioAnalyses.set(scenarioId, scenarioAnalysis);

        let groupAnalysis: GroupAnalysis;
        const items: AnalysisItem[] = [];

        if (groupType === GroupType.Scenario) {
          groupAnalysis = new GroupAnalysis(scenarioId, scenarioIndex, scenarioData.name!, analysisType, groupType);
          attributeValues.forEach((value: string) => {
            const displayName = this.getAttributeDisplayName(value, attributeField);
            items.push(new AnalysisItem(value, displayName));
          });
          scenarioAnalysis.groupData.push(groupAnalysis);
        }

        scenarioData.modelDatas?.forEach((modelData: ModelData, modelIndex: number) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {

            if (groupType === GroupType.Model) {
              groupAnalysis = new GroupAnalysis(modelData.model!.modelId!, modelIndex, modelData.model!.apsItemName!, analysisType, groupType);
              items.length = 0;
              attributeValues.forEach((value: string) => {
                const displayName = this.getAttributeDisplayName(value, attributeField);
                items.push(new AnalysisItem(value, displayName));
              });
              scenarioAnalysis.groupData.push(groupAnalysis);
            }

            const modelIdentifier = (modelData.model?.apsViewId || modelData.model?.modelId) as string;
            const groupCadIdsMap = new Map(Array.from(attributeValues).map((key) => [key, [] as CadIds]));
            scenarioAnalysis.modelGroupCadIdsMap.set(modelIdentifier, groupCadIdsMap);

            // Process element data
            modelData.elementDatas?.forEach((elementData: ElementData) => {
              if ((elementData.componentCount || 0) > 0) {
                return;
              }

              const attributeValue = this.getAttributeValue(elementData, attributeField);
              if (!attributeValue) return;

              const item = items.find((item) => item.groupKey === attributeValue);
              if (item) {
                item.elementCount = item.elementCount + 1;
                item.co2eTotal = (item.co2eTotal || 0) + (elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                item.minCo2e = Math.min(item.minCo2e, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);
                item.maxCo2e = Math.max(item.maxCo2e, elementData.getInScopeTotalCo2(this.totalCo2Config) || 0);

                const cadId = (elementData.parentCadId || elementData.cadId!)?.replace(/\.[^/.]+$/, "");
                groupCadIdsMap.get(attributeValue)?.push(cadId);
              }
            });

            if (groupType === GroupType.Model) {
              items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
            }
          }
        });

        if (groupType === GroupType.Scenario) {
          items.forEach((item) => groupAnalysis.items.set(item.groupKey, item));
        }
      }
    });
  };

  private getAnalysisTypeFromAttributeField = (attributeField: string): AnalysisType => {
    switch (attributeField) {
      case 'system':
        return AnalysisType.System;
      case 'subSystem':
        return AnalysisType.SubSystem;
      case 'freeText1':
        return AnalysisType.Attribute1;
      case 'freeText2':
        return AnalysisType.Attribute2;
      case 'freeText3':
        return AnalysisType.Attribute3;
      default:
        return AnalysisType.System;
    }
  };

  private getAllAttributeValues = (
    projectData: ProjectData,
    scenarioIds: string[],
    selectionManager: SelectionManager,
    attributeField: 'system' | 'subSystem' | 'freeText1' | 'freeText2' | 'freeText3'): Set<string> => {
    const values = new Set<string>();

    projectData?.scenarioDatas?.forEach((scenarioData: ScenarioData) => {
      const scenarioId = scenarioData.scenarioId as string;
      if (scenarioIds.includes(scenarioId)) {
        scenarioData.modelDatas?.forEach((modelData: ModelData) => {
          if (this.isModelSelected(selectionManager, scenarioId, modelData)) {
            modelData.elementDatas?.forEach((elementData: ElementData) => {
              const value = this.getAttributeValue(elementData, attributeField);
              if (value) {
                values.add(value);
              }
            });
          }
        });
      }
    });

    return values;
  };

  private getAttributeValue = (elementData: ElementData, attributeField: string): string | null => {
    switch (attributeField) {
      case 'system':
        return elementData.system || null;
      case 'subSystem':
        return elementData.subSystem || null;
      case 'freeText1':
        return elementData.freeText1 || null;
      case 'freeText2':
        return elementData.freeText2 || null;
      case 'freeText3':
        return elementData.freeText3 || null;
      default:
        return null;
    }
  };

  private getAttributeDisplayName = (value: string, _attributeField: string): string => {
    // Name resolution is now handled in the UI components using metadata entities
    return value;
  };

  private getCadMaterialGroupKey = (elementData: ElementData): string | null => {
    // Supplemental types must always be grouped under virtual CAD material rows.
    if (elementData.type === MaterialType.RebarSupplemental) {
      return EstimatedRebarCadMaterial;
    }

    if (elementData.type === MaterialType.NonModelledSupplemental) {
      return EstimatedNonModelledCadMaterial;
    }

    if (elementData.cadMaterial) {
      return elementData.cadMaterial;
    }

    return null;
  };

  private getCo2MaterialTypeGroupKey = (elementData: ElementData): string => {
    if (elementData.co2MaterialTypeId) {
      return elementData.co2MaterialTypeId;
    }

    // Keep fallback keys for supplemental elements that may not have a material type id.
    if (elementData.type === MaterialType.RebarSupplemental) {
      return EstimatedRebarCo2MaterialTypeId;
    }

    if (elementData.type === MaterialType.NonModelledSupplemental) {
      return EstimatedNonModelledCo2MaterialTypeId;
    }

    return EmptyGuid;
  };

  private reset = () => {
    this.analyses.clear();
    this.selections.clear();
  };
}

/**
 * Helper class to configure which TotalCo2 columns to include in the analysis.
 * The configuration is based on the columns selected in the UI.
 */
export class TotalCo2Configurator {
  a1A2A3: string[] = ['a1A2A3'];
  a4A5: string[] = ['a4', 'a5'];
  a: string[] = ['a1A2A3', 'a4', 'a5'];
  b1: string[] = ['b1'];
  b2B5: string[] = ['b2', 'b3', 'b4', 'b5'];
  biogenic: string[] = ['biogenic'];
  b: string[] = ['b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'biogenic'];
  c: string[] = ['c1', 'c2', 'c3', 'c4'];
  d: string[] = ['d'];
  totalCo2: string[] = ['totalCo2'];

  getTotalCo2Config = (columns: string[]): string[] => {
    if (columns.includes('totalCo2')) {
      return this.totalCo2;
    }
    
    return Array.from(columns.reduce((acc: Set<string>, col: string) => {
      const values = this[col as keyof TotalCo2Configurator] as string[];
      values.forEach(acc.add, acc);
      return acc;
    }, new Set<string>));
  };
}

export const projectAnalysis = new ProjectAnalysis();